/**
 * agentLoop.js — the JSON action-loop that drives the LiTeCAB Copilot.
 *
 * Each step: build a system prompt with a fresh state snapshot → call Gemini
 * (which returns JSON) → parse one tool call or a final message → execute the
 * tool via the registry → feed the observation back → repeat. Reuses the
 * existing geminiService.callGemini (responseMimeType: application/json) and the
 * Cloudflare proxy unchanged.
 */

import { callGemini } from '../geminiService';
import { getState, callAction } from './actionBridge';
import { buildSystemPrompt, summarizeState, AGENT_ACK } from './agentPrompts';
import { getToolCatalog, getTool, runTool } from './toolRegistry';

const MAX_STEPS = 8;
const OBS_LIMIT = 1800; // cap observation size fed back to the model (token control)

/** Tolerant parse: strip fences and, if needed, extract the first {...} block. */
export function parseAgentJson(text) {
  let clean = (text || '').trim();
  if (clean.startsWith('```')) clean = clean.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  if (!clean.startsWith('{')) {
    const start = clean.indexOf('{');
    const end = clean.lastIndexOf('}');
    if (start >= 0 && end > start) clean = clean.slice(start, end + 1);
  }
  return JSON.parse(clean);
}

function safeJson(obj) {
  let s;
  try { s = JSON.stringify(obj); } catch { s = String(obj); }
  if (s.length > OBS_LIMIT) s = s.slice(0, OBS_LIMIT) + '…[truncated]';
  return s;
}

/**
 * Run the agent to completion (or until the step cap).
 *
 * @param {object}   opts
 * @param {Array}    opts.history   prior conversation [{role:'user'|'assistant', text}], incl. the new user turn
 * @param {Function} opts.onStep    callback for live UI: {type:'action'|'observation'|'final', ...}
 * @param {number}   opts.maxSteps  hard cap on tool calls (default 8)
 * @returns {Promise<{text, steps, capped?, error?}>}
 */
export async function runAgent({ history, onStep = () => {}, maxSteps = MAX_STEPS }) {
  const working = [...history];
  const catalog = getToolCatalog();

  for (let step = 0; step < maxSteps; step++) {
    const systemPrompt = buildSystemPrompt(catalog, summarizeState(getState()));

    let raw;
    try {
      raw = await callGemini(working, { systemPrompt, systemAck: AGENT_ACK });
    } catch (err) {
      const text = `I couldn't reach the AI service: ${err.message}`;
      onStep({ type: 'final', text, error: true });
      return { text, error: true, steps: step };
    }

    let parsed;
    try {
      parsed = parseAgentJson(raw);
    } catch (err) {
      working.push({ role: 'assistant', text: raw });
      working.push({ role: 'user', text: `OBSERVATION: Your reply was not a single valid JSON object (${err.message}). Reply with exactly one JSON object per the protocol.` });
      continue;
    }

    // Final message → done.
    if (parsed.final !== undefined && !parsed.action) {
      const text = String(parsed.final);
      onStep({ type: 'final', text, thought: parsed.thought });
      return { text, steps: step + 1 };
    }

    if (!parsed.action || typeof parsed.action !== 'string') {
      working.push({ role: 'assistant', text: raw });
      working.push({ role: 'user', text: 'OBSERVATION: Missing "action" or "final". Reply with one JSON object.' });
      continue;
    }

    const tool = getTool(parsed.action);
    const args = parsed.args || {};
    onStep({ type: 'action', name: parsed.action, args, thought: parsed.thought, kind: tool?.kind });

    if (!tool) {
      working.push({ role: 'assistant', text: safeJson({ action: parsed.action, args }) });
      working.push({ role: 'user', text: `OBSERVATION: Unknown tool "${parsed.action}". Available: ${catalog.map((t) => t.name).join(', ')}` });
      continue;
    }

    // Snapshot before writes so the user can undo (no-op until undo is wired).
    if (tool.kind === 'write') {
      try { await callAction('snapshot', { label: parsed.action }); } catch (_) { /* undo not wired yet */ }
    }

    const observation = await runTool(parsed.action, args);
    onStep({ type: 'observation', name: parsed.action, observation, kind: tool.kind });

    working.push({ role: 'assistant', text: safeJson({ action: parsed.action, args }) });
    working.push({ role: 'user', text: `OBSERVATION (${parsed.action}): ${safeJson(observation)}` });
  }

  const text = "I've taken several steps but haven't finished — could you confirm what you'd like me to do next?";
  onStep({ type: 'final', text, capped: true });
  return { text, steps: maxSteps, capped: true };
}

export default { runAgent };
